import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Axios from 'axios';
import moment from 'moment';
import intlShape from '@/shared/utils/intlShape';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import { FaFileExcel, FaCog } from 'react-icons/fa';
import FileSaver from 'file-saver';
import SimplePanel from '@/shared/components/SimplePanel';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import api from '@/shared/api';
import Checkbox from '@/shared/components/input/Checkbox';
import SignalRService from '@/shared/services/SignalRService';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import { getRegistrationModalPropsForTask } from '@/shared/components/registration/registrationHelpers';
import Button from '@/shared/components/Button';
import utils from '@/shared/utils/adminpulse';
import handleHttpError from '@/shared/services/HandleHttpError';
import { AddIcon } from '@/shared/components/Icons';
import ToastrService from '@/shared/services/ToastrService';
import { CanCreate } from '@/shared/components/security';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import YesNoModal from '@/shared/components/YesNoModal';
import { injectStateService } from '@/shared/services/StateService';
import ShareItemModal from '@/shared/components/ShareItemModal';
import { getRelationName } from '@/shared/components/RelationName';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import SendEmailModal from '@/shared/components/email/MultiRelationsMailModal';
import SingleRelationMailModal from '@/shared/components/email/SingleRelationMailModal';

import {
  handleHiddenColumnsSelection,
  initHiddenColumnsState,
} from '@/shared/utils/hiddenColumnsHelper';
import { getShareItemNameForTask } from '@/shared/services/ObjectDisplayService';
import TaskDetailsModal from '../taskDetails/TaskDetailsModal';
import CreateToDoModal from '../CreateToDoModal';
import TaskListFilters, { defaultFilters } from './TaskListFilters';
import messages from '../taskMessages';
import TaskListRow from './TaskListRow';
import config from '../../../config';
import ChangeAssignedUserModal from './ChangeAssignedUserModal';
import TaskListGlobalActionsButton from './TaskListGlobalActionsButton';
import ChangeTaskDeadlineModal from '../ChangeTaskDeadlineModal';
import ChangeTaskTagsModal from '../ChangeTaskTagsModal';
import AuthService from '@/shared/services/AuthService';
import HasFeature from '@/shared/components/security/HasFeature';
import ChangeTaskExecutionDateModal from '@/modules/tasks/ChangeTaskExecutionDateModal';
import { getPeriodStartEndDates } from '@/shared/components/PeriodButtonGroup';

const columnSelectionStorageKey = 'TaskListHiddenColumns';
const columnDefinitions = [
  { id: 'assignedTo', message: messages.AssignedTo, visibleByDefault: true },
  { id: 'relation', message: messages.Relation, visibleByDefault: true },
  { id: 'customerCode', message: messages.CustomerCode, visibleByDefault: false },
  {
    id: 'assignment',
    message: messages.Assignment,
    visibleByDefault: true,
    enabled: () => hasAssignments(),
  },
  { id: 'status', message: messages.TaskStatus, visibleByDefault: true },
  {
    id: 'executionDate',
    message: messages.ExecutionDate,
    visibleByDefault: false,
    enabled: () => AuthService.hasFeature('CapacityPlanning'),
  },
  {
    id: 'executionDuration',
    message: messages.ExecutionDuration,
    visibleByDefault: false,
    enabled: () => AuthService.hasFeature('CapacityPlanning'),
  },
  { id: 'deadline', message: messages.Deadline, visibleByDefault: true },
  { id: 'createdAt', message: messages.CreatedAt, visibleByDefault: false },
  { id: 'createdBy', message: messages.CreatedBy, visibleByDefault: false },
  { id: 'modifiedAt', message: messages.ModifiedAt, visibleByDefault: false },
  { id: 'modifiedBy', message: messages.ModifiedBy, visibleByDefault: false },
  { id: 'completedDate', message: messages.CompletedDate, visibleByDefault: false },
  { id: 'duration', message: messages.RegistratedTime, visibleByDefault: true },
  {
    id: 'referenceYear',
    message: messages.ReferenceYear,
    visibleByDefault: false,
    enabled: () => AuthService.hasFeature('ReferenceYear'),
  },
];

class TaskListContainer extends Component {
  state = {
    allTasksSelected: false,
    items: [],
    initialSearchParams: {
      sortKey: 'deadline',
      filter: {},
      page: 0,
      pageSize: 50,
      desc: true,
      taskFilters: {
        ...defaultFilters,
      },
    },
    currentSearchParams: undefined,
    showColumnSelectionModal: false,
    columnStatus: {},
  };

  constructor(props) {
    super(props);
    let searchParams = {};
    let filters = {};
    if (props.searchParams && typeof props.searchParams.filters === 'object') {
      // Parameters from state
      filters = { ...props.searchParams.filters };
      if (props.desc || props.desc === false) searchParams.desc = props.desc;
      if (props.sort) searchParams.sortKey = props.sort;
    } else if (props.taskId) {
      filters = { plannedTaskId: props.taskId };
      this.state.showTaskDetailsModal = true;
      this.state.activeTask = { id: props.taskId };
    } else {
      // Parameters from memory
      const { taskFilters: memoryTaskFilters = {}, ...otherParams } =
        JSON.parse(sessionStorage.getItem('TaskListParams')) || {};

      searchParams = otherParams;
      filters = memoryTaskFilters;
    }
    // Fix filter formats
    if (filters.deadlineFrom) filters.deadlineFrom = moment(filters.deadlineFrom);
    if (filters.deadlineUntil) filters.deadlineUntil = moment(filters.deadlineUntil);
    if (filters.executionDateFrom) filters.executionDateFrom = moment(filters.executionDateFrom);
    if (filters.executionDateUntil) filters.executionDateUntil = moment(filters.executionDateUntil);
    if (filters.period) {
      const [dateFrom, dateUntil, period] = getPeriodStartEndDates(filters.period);
      filters.deadlineFrom = dateFrom;
      filters.deadlineUntil = dateUntil;
      filters.period = period;
    }

    const taskFilters = {
      ...defaultFilters,
      ...filters,
    };
    const { hideableColumns, columnStatus } = initHiddenColumnsState(
      columnSelectionStorageKey,
      columnDefinitions,
      props.intl,
    );
    this.state.hideableColumns = hideableColumns;
    this.state.columnStatus = columnStatus;

    const { initialSearchParams: orgSearchParams } = this.state;
    this.state.initialSearchParams = {
      ...orgSearchParams,
      ...searchParams,
      taskFilters,
    };
  }

  taskUpdatedEventHandler = task => {
    if (!task) return;
    const { items } = this.state;

    this.setState(state => {
      const index = items.findIndex(x => x.id === task.id);
      if (index !== -1) {
        items[index] = { ...items[index], ...task };

        if (task.children && task.children.length) {
          task.children.map(subtask => {
            const childIndex = items.findIndex(x => x.id === subtask.id);
            if (index !== -1) {
              items[childIndex] = { ...items[childIndex], ...subtask };
            }
          });
        }
        if (task.parent) {
          const parentIndex = items.findIndex(x => x.id === task.parent.id);
          if (index !== -1) {
            items[parentIndex] = {
              ...items[parentIndex],
              ...task.parent,
            };
          }
        }
        return {
          ...state,
          items,
        };
      }
      return {
        ...state,
      };
    });
  };

  taskRemovedEventHandler = taskIds => {
    const { items } = this.state;
    const itemsCopy = items.filter(
      task => !taskIds.includes(task.id) && !taskIds.includes(task.parentId),
    );

    this.setState(state => ({
      ...state,
      items: [...itemsCopy],
    }));
  };

  handleTaskHasAttachmentsMessage = message => {
    const { items } = this.state;

    this.setState(state => {
      const index = items.findIndex(x => x.id === message.id);
      if (index !== -1) {
        items[index] = {
          ...items[index],
          hasAttachments: message.hasAttachments,
        };
      }
      // update parent id present
      if (message.parentId) {
        const parentIndex = items.findIndex(x => x.id === message.parentId);
        if (parentIndex !== -1) {
          items[parentIndex] = {
            ...items[parentIndex],
            hasAttachments: message.parentHasAttachments,
          };
        }
      }

      return {
        ...state,
        items,
      };
    });
  };

  componentDidMount() {
    SignalRService.subscribe('taskUpdated', this.taskUpdatedEventHandler);
    SignalRService.subscribe('taskDeleted', this.taskRemovedEventHandler);
    SignalRService.subscribe('taskHasAttachmentsChanged', this.handleTaskHasAttachmentsMessage);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('taskUpdated', this.taskUpdatedEventHandler);
    SignalRService.unsubscribe('taskDeleted', this.taskRemovedEventHandler);
    SignalRService.unsubscribe('taskHasAttachmentsChanged', this.handleTaskHasAttachmentsMessage);

    if (this.loadListToken && !this.loadListToken.token.reason) {
      this.loadListToken.cancel('Operation canceled due to leaving page.');
    }
  }

  static propTypes = {
    intl: intlShape.isRequired,
  };

  loadItems = searchParams => {
    const endpoint = `${config.appUrls.tasks}/api/TaskList`;
    const { allTasksSelected } = this.state;
    const { intl } = this.props;
    if (this.loadListToken && !this.loadListToken.token.reason) {
      this.loadListToken.cancel('Operation canceled due to new request.');
    }
    this.loadListToken = Axios.CancelToken.source();

    sessionStorage.setItem(
      'TaskListParams',
      JSON.stringify({
        taskFilters: searchParams.taskFilters,
        sortKey: searchParams.sortKey,
        desc: searchParams.desc,
      }),
    );

    return api.base
      .post(
        endpoint,
        {
          ...searchParams.taskFilters,
          page: searchParams.page,
          take: searchParams.pageSize,
          sort: searchParams.sortKey,
          desc: searchParams.desc,
        },
        {
          cancelToken: this.loadListToken.token,
        },
      )
      .then(({ data }) => {
        // If checkbox for all tasks is selected, set selected on all new incoming tasks
        const newTasks = data.tasks.map(e => ({ ...e, selected: !!allTasksSelected }));

        this.setState(state => ({
          ...state,
          items: searchParams.page > 0 ? [...state.items, ...newTasks] : [...newTasks],
          total: data.total,
          currentSearchParams: searchParams,
        }));
        return data.tasks;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) {
          return handleHttpError(intl)(error);
        }
      });
  };

  handleTaskSelection = task => {
    this.setState(({ items }) => ({
      items: items.map(x => (x.id === task.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  filtersChanged = filters => {
    this.setState(state => ({
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        taskFilters: filters,
      },
    }));
  };

  handleSelectAllTasks = () => {
    this.setState(({ items, allTasksSelected }) => ({
      allTasksSelected: !allTasksSelected,
      items: items.map(e => ({ ...e, selected: !allTasksSelected })),
    }));
  };

  getCountSelectedTasks() {
    const { allTasksSelected, total, items } = this.state;

    const count = items ? items.filter(x => x.selected !== allTasksSelected).length : 0;

    return allTasksSelected ? total - count : count;
  }

  getCountSelectedTasksFormatted() {
    const { total } = this.state;
    const { intl } = this.props;
    const count = this.getCountSelectedTasks();

    return intl.formatMessage(messages.CountTasksSelected, { count, total });
  }

  isGlobalActionsDisabled = () => {
    const { items, allTasksSelected } = this.state;
    if (!items || !items.length) return true;
    if (items.some(x => x.selected) || allTasksSelected) return false;

    return true;
  };

  onAddRegistrationClick = task => {
    this.setState(state => ({
      ...state,
      activeTask: task,
      showRegistrationModal: true,
    }));
  };

  onRegistrationCreated = registration => {
    const { items, activeTask } = this.state;
    const itemsCopy = [...items];

    const item = itemsCopy.find(x => x.id === activeTask.id);
    if (item) {
      item.duration += registration.durationMinutes;
    }

    this.setState(state => ({
      ...state,
      showRegistrationModal: false,
      items: itemsCopy,
    }));
  };

  createReport = () => {
    const { intl } = this.props;
    const { currentSearchParams, items, allTasksSelected } = this.state;

    const model = {
      ...currentSearchParams.taskFilters,
      page: currentSearchParams.page,
      take: currentSearchParams.pageSize,
      sort: currentSearchParams.sortKey,
      desc: currentSearchParams.desc,
      selectAll: allTasksSelected,
      selectedTasks: items ? items.filter(x => x.selected !== allTasksSelected).map(x => x.id) : [],
    };

    const culture = intl.locale;
    const connectionId = SignalRService.getConnectionId();

    const endpoint = `${config.appUrls.tasks}/api/tasklist/export?culture=${culture}&connectionId=${connectionId}`;
    this.setState(state => ({
      ...state,
      creatingReport: true,
    }));

    return api.base
      .post(endpoint, model)
      .then(result => {
        ToastrService.info(intl.formatMessage(messages.ExportToExcelStarted));
        this.setState(state => ({
          ...state,
          creatingReport: false,
        }));
      })
      .catch(error => {
        this.setState(state => ({
          ...state,
          creatingReport: false,
        }));
        handleHttpError(intl)(error);
      });
  };

  onToDoCreated = toDo => {
    const { items } = this.state;

    const itemsCopy = [toDo, ...items];

    this.setState(state => ({
      ...state,
      showCreateToDoModal: false,
      items: itemsCopy,
    }));
  };

  onShowTaskDetails = task => {
    this.setState({
      activeTask: task,
      showTaskDetailsModal: true,
    });
  };

  onTaskDetailsClosed = () => {
    this.setState({
      showTaskDetailsModal: false,
    });
  };

  onRequestDeleteTask = task => {
    this.setState(state => ({
      ...state,
      activeTask: task,
      showDeleteTaskModal: true,
    }));
  };

  getSelectedRelationLanguages = () => {
    const { allTasksSelected, items } = this.state;

    return new Promise((resolve, reject) => {
      if (allTasksSelected) {
        const { currentSearchParams } = this.state;
        const model = {
          ...currentSearchParams.taskFilters,
          selectAll: allTasksSelected,
          selectedTasks: items
            ? items.filter(x => x.selected !== allTasksSelected).map(x => x.id)
            : [],
        };

        api.base
          .post(`${config.appUrls.tasks}/api/tasklist/languages`, model)
          .then(({ data }) => {
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        const languages = [
          ...new Set(items.filter(x => x.selected).map(x => x.relation.languageCode)),
        ];
        resolve(languages);
      }
    });
  };
  openSendEmailModal = () => {
    const { intl } = this.props;
    this.getSelectedRelationLanguages()
      .then(languages => {
        this.setState({
          showSendMailModal: true,
          languages,
        });
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  deleteTask = () => {
    const { activeTask, items } = this.state;
    const { intl } = this.props;

    const endpoint = `${config.appUrls.tasks}/api/PlannedTasks/${activeTask.id}`;

    return api.base
      .delete(endpoint, { params: { taskLevel: activeTask.taskLevel } })
      .then(({ data }) => {
        ToastrService.success(intl.formatMessage(messages.TaskDeleted));

        let itemsCopy = items;

        if (data.taskLevel === 0) {
          // If a main task was deleted, remove sub tasks from items if present
          itemsCopy = items.filter(
            task => task.id !== activeTask.id && task.parentId !== activeTask.id,
          );
        } else {
          itemsCopy = items.filter(task => task.id !== activeTask.id);
        }

        this.setState(state => ({
          ...state,
          showDeleteTaskModal: false,
          items: [...itemsCopy],
        }));
      })
      .catch(error => {
        this.setState(state => ({
          ...state,
          showDeleteTaskModal: false,
        }));
        handleHttpError(intl)(error);
      });
  };

  // This method can return multiple tasks because multiple tasks could have been opened after another
  // (clicking parent or subtask -> vsts-like)
  onTaskDetailModalClosed = tasks => {
    if (tasks && tasks.length) {
      this.setState(
        state => ({
          ...state,
          showTaskDetailsModal: false,
        }),
        () => tasks.map(task => this.taskUpdatedEventHandler(task)),
      );
    } else {
      this.setState(state => ({
        ...state,
        showTaskDetailsModal: false,
      }));
    }
  };
  handleTasksUpdated = updatedTasks => {
    if (!updatedTasks || !updatedTasks.length) return;
    this.setState(state => ({
      items: state.items.map(row => {
        const updated = updatedTasks.find(x => x.id === row.id);
        if (!updated) return row;
        return { ...row, ...updated };
      }),
    }));
  };

  handleSendEmail = task => {
    this.setState({
      selectedRelationId: task.relationId,
      selectedCompanyId: task.relation.companyId,
      selectedInvoiceStreamId: task.invoiceStreamId,
      activeTask: task,
      showSendByEmailModal: true,
    });
  };

  updateTask = task => {
    const { intl } = this.props;
    task.tags = null;
    return api.base.put(`${config.appUrls.tasks}/api/plannedTasks`, task).then(
      ({ data }) => {
        ToastrService.changesSaved(intl);
        this.setState(({ items }) => ({
          items: items.map(x => (x.id === task.id ? { ...x, ...data } : x)),
        }));
        return data;
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  updateDeadline = task => {
    const { intl } = this.props;
    const deadline = task.deadline ? task.deadline.format('YYYY-MM-DD') : '';
    return api.base
      .put(`${config.appUrls.tasks}/api/plannedTasks/deadline/${task.id}/${deadline}`, task)
      .then(
        ({ data }) => {
          ToastrService.changesSaved(intl);
          this.setState(({ items }) => ({
            items: items.map(x => (x.id === task.id ? { ...x, ...data } : x)),
          }));
          return data;
        },
        error => {
          handleHttpError(intl)(error);
        },
      );
  };

  fieldUpdate = (task, url) => {
    const { intl } = this.props;
    return api.base.put(url, task).then(
      ({ data }) => {
        ToastrService.changesSaved(intl);
        this.setState(({ items }) => ({
          items: items.map(x => (x.id === task.id ? { ...x, ...data } : x)),
        }));
        return data;
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  updateExecutionDate = task => {
    const executionDate = task.executionDate
      ? typeof task.executionDate === 'string'
        ? task.executionDate
        : task.executionDate.format('YYYY-MM-DD')
      : '';
    const url = `${config.appUrls.tasks}/api/plannedTasks/executionDate/${task.id}/${executionDate}`;
    return this.fieldUpdate(task, url);
  };

  updateExecutionDuration = task => {
    const executionDuration = task.executionDuration;
    const url = `${config.appUrls.tasks}/api/plannedTasks/executionDuration/${task.id}/${executionDuration}`;
    return this.fieldUpdate(task, url);
  };

  updateUser = task => {
    const { intl } = this.props;

    const model = { taskId: task.id, userId: task.userId, taskLevel: task.taskLevel };
    return api.base.put(`${config.appUrls.tasks}/api/plannedTasks/UpdateAssignedUser`, model).then(
      ({ data }) => {
        ToastrService.changesSaved(intl);
        this.setState(({ items }) => ({
          items: items.map(x => (x.id === task.id ? { ...x, ...data } : x)),
        }));
        return data;
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  apiCall = (endpoint, model) => {};

  executeBatchActionChangeAssignedUser = assignedToModel => {
    const { intl, stateService } = this.props;
    const batchActionModel = this.createBatchActionModel();
    const model = {
      ...batchActionModel,
      parameters: assignedToModel,
    };

    const endpoint = `${config.appUrls.tasks}/api/TaskList/BatchChangeAssignedUser`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showChangeAssignedUserModal: false,
        }));
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(
          intl.formatMessage(messages.UpdateAssignedUserQueued),
          undefined,
          onClick,
        );
      })
      .catch(error => {
        this.setState(state => ({
          ...state,
          showChangeAssignedUserModal: false,
        }));
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionChangeDeadline = ({ deadline }) => {
    const { intl, stateService } = this.props;
    const batchActionModel = this.createBatchActionModel();
    const model = {
      ...batchActionModel,
      deadline,
    };

    const endpoint = `${config.appUrls.tasks}/api/TaskList/BatchChangeDeadline`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState({
          showChangeTaskDeadlineModal: false,
        });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(intl.formatMessage(messages.BackgroundJobQueued), undefined, onClick);
      })
      .catch(error => {
        this.setState({
          showChangeTaskDeadlineModal: false,
        });
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionChangeExecutionDate = ({ executionDate }) => {
    const { intl, stateService } = this.props;
    const batchActionModel = this.createBatchActionModel();
    const model = {
      ...batchActionModel,
      executionDate,
    };

    const endpoint = `${config.appUrls.tasks}/api/TaskList/BatchChangeExecutionDate`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState({
          showChangeTaskExecutionDateModal: false,
        });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(intl.formatMessage(messages.BackgroundJobQueued), undefined, onClick);
      })
      .catch(error => {
        this.setState({
          showChangeTaskExecutionDateModal: false,
        });
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionSendTaskRelationMail = emailDto => {
    const { intl, stateService } = this.props;
    const batchActionModel = this.createBatchActionModel();
    const model = {
      ...batchActionModel,
      parameters: emailDto,
    };

    const endpoint = `${config.appUrls.tasks}/api/TaskList/BatchMailTaskRelation`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState({
          showSendMailModal: false,
        });

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(intl.formatMessage(messages.BackgroundJobQueued), undefined, onClick);
      })
      .catch(error => {
        this.setState({
          showSendMailModal: false,
        });
        handleHttpError(intl)(error);
      });
  };

  executeBatchActionDeleteTasks = () => {
    const { intl } = this.props;
    const model = this.createBatchActionModel();

    const endpoint = `${config.appUrls.tasks}/api/TaskList/BatchDeleteTasks`;

    return api.base
      .post(endpoint, model)
      .then(() => {
        this.setState({ showBatchDeleteTaskModal: false });
      })
      .catch(error => {
        this.setState({ showBatchDeleteTaskModal: false });
        handleHttpError(intl)(error);
      });
  };
  executeBatchActionChangeTags = batchActionParameters => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl, stateService } = this.props;

    const model = { ...batchActionParameters, ...this.createBatchActionModel() };
    const endpoint = `${config.appUrls.tasks}/api/TaskList/BatchChangeTags`;

    return api.base
      .post(endpoint, model)
      .then(({ data }) => {
        this.setState({
          showChangeTagsModal: false,
        });
        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;
        ToastrService.info(intl.formatMessage(messages.BackgroundJobQueued), undefined, onClick);
      })
      .catch(error => {
        handleHttpError(intl)(error);
      });
  };

  createBatchActionModel = () => {
    const { currentSearchParams, items, allTasksSelected } = this.state;

    const model = {
      filterParams: {
        ...currentSearchParams.taskFilters,
      },
      page: currentSearchParams.page,
      take: currentSearchParams.pageSize,
      sort: currentSearchParams.sortKey,
      desc: currentSearchParams.desc,
      selectAll: allTasksSelected,
      selectedTasks: items ? items.filter(x => x.selected !== allTasksSelected).map(x => x.id) : [],
    };

    return model;
  };

  handleColumnSelection = items => {
    this.setState(({ hideableColumns }) => ({
      ...handleHiddenColumnsSelection(items, hideableColumns, columnSelectionStorageKey),
      showColumnSelectionModal: false,
    }));
  };

  hasTasks = () => {
    const { initialSearchParams } = this.state;
    if (!initialSearchParams) return true;
    const { taskLevels = [] } = initialSearchParams.taskFilters;
    return taskLevels.length === 0 || taskLevels.indexOf(0) >= 0 || taskLevels.indexOf(1) >= 0;
  };
  hasTodos = () => {
    const { initialSearchParams } = this.state;
    if (!initialSearchParams) return true;
    const { taskLevels = [] } = initialSearchParams.taskFilters;
    return taskLevels.length === 0 || taskLevels.indexOf(2) >= 0;
  };

  renderRow = item => {
    const { columnStatus } = this.state;
    return (
      <TaskListRow
        key={item.id}
        task={item}
        columnStatus={columnStatus}
        handleTaskSelection={this.handleTaskSelection}
        onAddRegistrationClick={this.onAddRegistrationClick}
        onShowTaskDetails={this.onShowTaskDetails}
        onRequestDeleteTask={this.onRequestDeleteTask}
        onTasksUpdated={this.handleTasksUpdated}
        onSendEmail={this.handleSendEmail}
        onShareItem={() => this.setState({ showShareItemModal: true, activeTask: item })}
        onUpdateTask={task => this.updateTask(task)}
        onUpdateDeadline={task => this.updateDeadline(task)}
        onUpdateExecutionDate={task => this.updateExecutionDate(task)}
        onUpdateExecutionDuration={task => this.updateExecutionDuration(task)}
        onUpdateUser={task => this.updateUser(task)}
      />
    );
  };

  render() {
    const { intl } = this.props;
    const {
      initialSearchParams,
      items,
      allTasksSelected,
      showRegistrationModal,
      activeTask,
      creatingReport,
      showCreateToDoModal,
      showTaskDetailsModal,
      showDeleteTaskModal,
      showChangeAssignedUserModal,
      showBatchDeleteTaskModal,
      showShareItemModal,
      showChangeTaskDeadlineModal,
      showChangeTaskExecutionDateModal,
      showChangeTagsModal,
      showSendMailModal,
      showSendByEmailModal,
      showColumnSelectionModal,
      hideableColumns,
      columnStatus,
      languages,
      selectedRelationId,
      selectedCompanyId,
      selectedInvoiceStreamId,
    } = this.state;

    const hasData = items && items.length > 0;
    const showAssignments = hasAssignments();
    return (
      <div>
        {showDeleteTaskModal && (
          <YesNoModal
            intl={intl}
            show={showDeleteTaskModal}
            title={intl.formatMessage(messages.DeleteTask)}
            message={
              activeTask.taskLevel === 0
                ? intl.formatMessage(messages.DeleteMainTaskConfirmationMsg)
                : intl.formatMessage(messages.DeleteTaskConfirmationMsg)
            }
            onNo={() =>
              this.setState(state => ({
                ...state,
                showDeleteTaskModal: false,
                activeTask: undefined,
              }))
            }
            onSave={() => this.deleteTask()}
          />
        )}
        {showChangeTaskDeadlineModal && (
          <ChangeTaskDeadlineModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeTaskDeadlineModal: false,
              }))
            }
            onSave={this.executeBatchActionChangeDeadline}
          />
        )}
        {showChangeTaskExecutionDateModal && (
          <ChangeTaskExecutionDateModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeTaskExecutionDateModal: false,
              }))
            }
            onSave={this.executeBatchActionChangeExecutionDate}
          />
        )}
        {showSendMailModal && (
          <SendEmailModal
            intl={intl}
            show
            languages={languages}
            emailContext={11}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showSendMailModal: false,
              }))
            }
            onSave={this.executeBatchActionSendTaskRelationMail}
          />
        )}

        {showSendByEmailModal && (
          <SingleRelationMailModal
            intl={intl}
            show
            relationId={selectedRelationId}
            invoiceStreamId={selectedInvoiceStreamId}
            companyId={selectedCompanyId}
            task={activeTask}
            emailContext={11}
            onClose={() => this.setState({ showSendByEmailModal: false })}
            onSave={() => this.setState({ showSendByEmailModal: false })}
            type="task"
          />
        )}

        {showBatchDeleteTaskModal && (
          <YesNoModal
            intl={intl}
            show={showBatchDeleteTaskModal}
            title={intl.formatMessage(messages.DeleteTask)}
            message={intl.formatMessage(messages.DeleteMainTaskConfirmationMsg)}
            onNo={() =>
              this.setState(state => ({
                ...state,
                showBatchDeleteTaskModal: false,
              }))
            }
            onSave={() => this.executeBatchActionDeleteTasks()}
          />
        )}

        {showRegistrationModal && (
          <RegistrationModal
            show
            {...getRegistrationModalPropsForTask(activeTask)}
            onSave={this.onRegistrationCreated}
            onClose={() =>
              this.setState(state => ({
                ...state,
                activeTask: undefined,
                showRegistrationModal: false,
              }))
            }
          />
        )}
        {showChangeAssignedUserModal && (
          <ChangeAssignedUserModal
            show
            onSave={this.executeBatchActionChangeAssignedUser}
            intl={intl}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeAssignedUserModal: false,
              }))
            }
          />
        )}
        {showCreateToDoModal && (
          <CreateToDoModal
            show
            onSave={this.onToDoCreated}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showCreateToDoModal: false,
              }))
            }
          />
        )}
        {showTaskDetailsModal && (
          <TaskDetailsModal
            show
            task={{ id: activeTask?.id }}
            onSave={this.onTaskDetailModalClosed}
            onClose={this.onTaskDetailModalClosed}
          />
        )}
        {showShareItemModal && (
          <ShareItemModal
            show
            type={activeTask.taskLevel === 2 ? 'todo' : 'task'}
            id={activeTask.id}
            relationId={activeTask.relationId}
            relationName={getRelationName(intl, activeTask.relation)}
            itemName={getShareItemNameForTask(activeTask, intl)}
            onClose={() => this.setState({ showShareItemModal: false })}
          />
        )}
        {showChangeTagsModal && (
          <ChangeTaskTagsModal
            show
            onSave={this.executeBatchActionChangeTags}
            onClose={() => this.setState({ showChangeTagsModal: false })}
            addTags={showChangeTagsModal.addTags}
            intl={intl}
          />
        )}
        {showColumnSelectionModal && (
          <CheckboxListModal
            title={intl.formatMessage(messages.SelectColumns)}
            items={hideableColumns}
            onSave={this.handleColumnSelection}
            onClose={() => this.setState({ showColumnSelectionModal: false })}
            show={showColumnSelectionModal}
            sortByLabel={false}
          />
        )}
        <SimplePanel
          article="5434921696018"
          title={intl.formatMessage(messages.TaskList)}
          buttons={
            <CanCreate on="Todo">
              <Button
                btnClassName="btn btn-xs btn-inverse margin10R"
                onClick={() => this.setState({ showCreateToDoModal: true })}
              >
                <span className="margin5R">
                  <AddIcon />
                </span>
                {intl.formatMessage(messages.CreateNewToDo)}
              </Button>
            </CanCreate>
          }
        >
          <TaskListFilters
            filters={initialSearchParams.taskFilters}
            onFilterChange={filters => this.filtersChanged(filters)}
            countSelectedTasks={this.getCountSelectedTasksFormatted()}
          />
        </SimplePanel>

        <div className="row btn-row">
          <div className="col-md-6" />
          <div className="col-md-6 pull-right content-right">
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.setState({ showColumnSelectionModal: true })}
            >
              <span className="margin5R">
                <FaCog className="react-icon" />
              </span>
              <FormattedMessage {...messages.ColumnConfig} />
            </Button>
            <Button
              btnClassName="btn btn-xs btn-default margin10R"
              onClick={() => this.createReport()}
              disabled={!hasData || creatingReport}
            >
              <span className="margin5R">
                <FaFileExcel className="file-icon" />
              </span>
              {intl.formatMessage(messages.ExportToExcel)}
            </Button>
            <TaskListGlobalActionsButton
              onChangedAssignedUser={() => this.setState({ showChangeAssignedUserModal: true })}
              onDeleteTask={() => this.setState({ showBatchDeleteTaskModal: true })}
              onChangeDeadline={() => this.setState({ showChangeTaskDeadlineModal: true })}
              onChangeExecutionDate={() =>
                this.setState({ showChangeTaskExecutionDateModal: true })
              }
              onAddTaskTags={() => this.setState({ showChangeTagsModal: { addTags: true } })}
              onRemoveTaskTags={() => this.setState({ showChangeTagsModal: { addTags: false } })}
              onSendEmail={() => this.openSendEmailModal()}
              hasTasks={this.hasTasks()}
              hasTodos={this.hasTodos()}
              disabled={this.isGlobalActionsDisabled()}
            />
          </div>
        </div>
        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => this.renderRow(item)}
          noDataLabel={<FormattedMessage {...messages.NoTasksFound} />}
        >
          <ColumnHeader
            className="checkbox-column"
            title={
              <Checkbox
                extraClass="margin2L"
                onClick={() => this.handleSelectAllTasks()}
                value={allTasksSelected}
              />
            }
          />
          <ColumnHeader title={intl.formatMessage(messages.Task)} className="task-column" />
          <ColumnHeader
            title={intl.formatMessage(messages.AssignedTo)}
            className="col-md-2"
            visible={columnStatus.assignedTo}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Relation)}
            sortKey="relation"
            className="col-md-2"
            visible={columnStatus.relation}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.CustomerCode)}
            visible={columnStatus.customerCode}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.Assignment)}
            sortKey="assignmentName"
            visible={showAssignments && !!columnStatus.assignment}
          />

          <ColumnHeader
            title={intl.formatMessage(messages.TaskStatus)}
            sortKey="status"
            className="col-md-1 text-center"
            visible={columnStatus.status}
          />
          {AuthService.hasFeature('ReferenceYear') ? (
            <ColumnHeader
              title={intl.formatMessage(messages.ReferenceYear)}
              sortKey="year"
              className="col-md-1 text-center"
              visible={!!columnStatus.referenceYear}
            />
          ) : null}
          {AuthService.hasFeature('CapacityPlanning') && (
            <ColumnHeader
              title={intl.formatMessage(messages.ExecutionDate)}
              sortKey="executionDate"
              className="col-md-1 text-center"
              visible={!!columnStatus.executionDate}
            />
          )}
          {AuthService.hasFeature('CapacityPlanning') && (
            <ColumnHeader
              title={intl.formatMessage(messages.ExecutionDuration)}
              sortKey="executionDuration"
              className="col-md-1 text-center"
              visible={!!columnStatus.executionDuration}
            />
          )}
          <ColumnHeader
            title={intl.formatMessage(messages.Deadline)}
            sortKey="deadline"
            className="col-md-1 text-center"
            visible={columnStatus.deadline}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.CreatedAt)}
            sortKey="createdAt"
            className="col-md-1 text-center"
            visible={columnStatus.createdAt}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.CreatedBy)}
            className="col-md-1"
            visible={columnStatus.createdBy}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.ModifiedAt)}
            sortKey="modifiedAt"
            className="col-md-1 text-center"
            visible={columnStatus.modifiedAt}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.ModifiedBy)}
            className="col-md-1"
            visible={columnStatus.modifiedBy}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.CompletedDate)}
            sortKey="completedDate"
            className="col-md-1 text-center"
            visible={columnStatus.completedDate}
          />
          <ColumnHeader
            title={intl.formatMessage(messages.RegistratedTime)}
            sortKey="duration"
            className="col-md-1 text-center"
            visible={columnStatus.duration}
          />
          <ColumnHeader className="col-md-1 text-right" />
        </ScrollableTable>
      </div>
    );
  }
}

export default injectStateService(injectIntl(TaskListContainer));
